import React, { useEffect, useRef, useState } from 'react'
import { getErrorMessage } from '@/utils/getErrorMessage'
import {
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Flex,
  Card,
  Form,
  Input,
  Spin,
  message,
} from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { type Permission } from '@/data'
import { createPermission } from '@/api/permissions/createPermission'
import { getPermissions } from '@/api/permissions/getPermissions'
import { updatePermission } from '@/api/permissions/updatePermission'
import { normalizePermissionName } from '@/data'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'

const { Title } = Typography
const { Content } = Layout

async function findPermissionById(
  permissionId: string,
): Promise<Permission | null> {
  let page = 1
  let lastPage = 1

  do {
    const { data, meta } = await getPermissions({ page })
    const permission = data.find((entry) => String(entry.id) === permissionId)

    if (permission) {
      return permission
    }

    lastPage = meta.last_page
    page += 1
  } while (page <= lastPage)

  return null
}

const PermissionEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const auth = React.useContext(AuthContext)!

  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [guardName, setGuardName] = useState('')
  const canCreatePermissions = hasPermission(
    auth.user,
    'lop:ui:action:create:permissions',
  )
  const canUpdatePermissions = hasPermission(
    auth.user,
    'lop:ui:action:update:permissions',
  )
  const canSave = isEditMode ? canUpdatePermissions : canCreatePermissions

  const hasFetched = useRef(false)

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    ;(async () => {
      try {
        setLoading(true)
        if (!id) {
          return
        }

        const permission = id ? await findPermissionById(id) : null

        if (!permission) {
          message.error('Permission not found')
          navigate('/admin/permissions')
          return
        }

        setName(permission.name)
        setGuardName(permission.guard_name)
      } catch (err) {
        message.error('Failed to load permission')
        console.error(err)
      } finally {
        setLoading(false)
      }
    })()
  }, [id, navigate])

  const handleSave = async () => {
    if (!canSave) {
      return
    }

    const normalizedName = normalizePermissionName(name.trim())

    try {
      setSaving(true)
      if (isEditMode && id) {
        await updatePermission(id, {
          name: normalizedName,
          guard_name: guardName || undefined,
        })
        message.success('Permission updated')
      } else {
        await createPermission({
          name: normalizedName,
          ...(guardName ? { guard_name: guardName } : {}),
        })
        message.success('Permission created')
        navigate('/admin/permissions')
      }
    } catch (err) {
      const fallback = isEditMode
        ? 'Failed to update permission'
        : 'Failed to create permission'
      message.error(getErrorMessage(err, fallback))
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: 'Permissions', href: '/admin/permissions' },
            { title: isEditMode ? `Edit: ${name}` : 'New Permission' },
          ]}
        />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/permissions')}
          />
          <Title level={3} style={{ margin: 0 }}>
            {isEditMode ? `Edit Permission: ${name}` : 'Create New Permission'}
          </Title>
        </Flex>

        <Flex gap="large" style={{ marginTop: 16 }}>
          <Content style={{ flex: 1, minHeight: 400 }}>
            <Card title="Permission Info" style={{ maxWidth: 700 }}>
              <Form layout="vertical">
                <Form.Item label="Name">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter name"
                  />
                </Form.Item>
                <Form.Item label="Guard Name">
                  <Input
                    value={guardName}
                    onChange={(e) => setGuardName(e.target.value)}
                    placeholder="Optional guard name"
                  />
                </Form.Item>
              </Form>
            </Card>
            <Flex justify="flex-end" style={{ paddingTop: 16 }}>
              {canSave ? (
                <Button type="primary" onClick={handleSave} loading={saving}>
                  {isEditMode ? 'Save Changes' : 'Create Permission'}
                </Button>
              ) : null}
            </Flex>
          </Content>
        </Flex>
      </Flex>
    </div>
  )
}

export const PermissionEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  return <PermissionEditorContent key={id ?? 'new'} />
}
